import { useRequiredContext } from "@redotech/react-util/context";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { REDO_EFFECT_API_URL } from "@redotech/redo-merchant-app-common/config";
import {
  EffectApiClientContext,
  EffectApiClientProvider,
} from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import {
  createContext,
  memo,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { merchantQueryClient } from "../../react-query-client";
import {
  createFoldersCollection,
  createViewsCollection,
  type FoldersCollection,
  type ViewsCollection,
} from "../db/views-folders-collections";

interface SupportViewsFoldersCollections {
  views: ViewsCollection;
  folders: FoldersCollection;
}

export const SupportViewsFoldersContext = createContext<
  SupportViewsFoldersCollections | undefined
>(undefined);
SupportViewsFoldersContext.displayName = "SupportViewsFoldersContext";

export function useSupportViewsFoldersCollections(): SupportViewsFoldersCollections {
  return useRequiredContext(SupportViewsFoldersContext);
}

const SupportViewsFoldersStoreProvider = memo(
  function SupportViewsFoldersStoreProvider({
    children,
  }: {
    children: ReactNode;
  }) {
    const effectApi = useRequiredContext(EffectApiClientContext);

    const collections = useMemo(
      () => ({
        views: createViewsCollection({
          queryClient: merchantQueryClient,
          effectApi,
        }),
        folders: createFoldersCollection({
          queryClient: merchantQueryClient,
          effectApi,
        }),
      }),
      [effectApi],
    );

    return (
      <SupportViewsFoldersContext.Provider value={collections}>
        {children}
      </SupportViewsFoldersContext.Provider>
    );
  },
);

export const SupportViewsFoldersProvider = memo(
  function SupportViewsFoldersProvider({ children }: { children: ReactNode }) {
    const auth = useContext(AuthContext);

    return (
      <EffectApiClientProvider
        authToken={auth?.token}
        baseUrl={REDO_EFFECT_API_URL}
      >
        <SupportViewsFoldersStoreProvider>
          {children}
        </SupportViewsFoldersStoreProvider>
      </EffectApiClientProvider>
    );
  },
);
